import { UnauthenticatedPage } from '@/components/UnauthenticatedPage/UnauthenticatedPage.tsx'
import { PasswordRequirementsList } from '@/features/user/components/PasswordRequirementsList'
import { useActivateUser } from '@/features/user/hooks'
import { useFirebasePasswordValidator } from '@/features/user/hooks/useFirebasePasswordValidator'
import { zodResolver } from '@hookform/resolvers/zod'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material'
import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { checkActivationToken } from '@/features/user/api/user.service'
import { AuthenticationPageContainer } from '@/features/user/components'

export const Route = createFileRoute('/activate')({
  validateSearch: z.object({
    t: z.string()
  }),
  component: ActivateAccountComponent,
  beforeLoad: async ({ search: { t } }) => {
    const valid = await checkActivationToken(t)
    if (!valid) {
      throw redirect({ to: '/reset-password-expired' })
    }
  }
})

// Type-safe translation keys for this form
type ActivateTranslationKey =
  | 'routes.activate.main.errors.passwordRequired'
  | 'routes.activate.main.errors.confirmRequired'
  | 'routes.activate.main.errors.passwordMismatch'

// Create a Zod schema that will use Firebase validation
const ActivateSchema = z
  .object({
    password: z
      .string()
      .min(1, 'routes.activate.main.errors.passwordRequired' as ActivateTranslationKey),
    confirm: z
      .string()
      .min(1, 'routes.activate.main.errors.confirmRequired' as ActivateTranslationKey)
  })
  .refine(data => data.password === data.confirm, {
    message: 'routes.activate.main.errors.passwordMismatch' as ActivateTranslationKey,
    path: ['confirm']
  })

type ActivateFormData = z.infer<typeof ActivateSchema>

function ActivateAccountComponent() {
  const navigate = useNavigate()
  const { t: token } = Route.useSearch()
  const { t } = useTranslation('_routes')

  const [showPw, setShowPw] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [validatingToken, setValidatingToken] = useState(true)
  const [error, setError] = useState<string>('')

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm<ActivateFormData>({
    resolver: zodResolver(ActivateSchema),
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirm: ''
    }
  })

  const password = watch('password')

  useEffect(() => {
    if (!token) {
      setError(t('routes.activate.main.errors.linkMissed'))
      setValidatingToken(false)
      return
    }

    setValidatingToken(false)
  }, [token, t])

  const { requirements: passwordRequirements, isValid: isPasswordValid } =
    useFirebasePasswordValidator(password)

  const { mutate: activateUser, isPending } = useActivateUser({
    onSuccess: () => {
      navigate({ to: '/login' })
    },
    onError: error => {
      setError(error.message || t('routes.activate.main.failedActivation'))
    }
  })

  const onSubmit = async (data: ActivateFormData) => {
    setError('')

    if (!isPasswordValid || !token) {
      return
    }

    activateUser({
      token,
      newPassword: data.password
    })
  }

  if (validatingToken) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <Typography>{t('routes.activate.main.linkValidation')}</Typography>
      </Box>
    )
  }

  return (
    <AuthenticationPageContainer
      advertisingChip={t('routes.reset-password.main.advertising.chip')}
      advertisingTitle={t('routes.reset-password.main.advertising.title')}
      advertisingSubtitle={t('routes.reset-password.main.advertising.subTitle')}
    >
      <UnauthenticatedPage>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'left',
            paddingLeft: {
              sm: 6,
              xs: 0
            },
            paddingRight: {
              xl: 30,
              sm: 8,
              xs: 0
            }
          }}
        >
          <Grid
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'top'
            }}
          >
            <Typography variant="h1" color="primary" component="span">
              {t('routes.activate.main.title')}
            </Typography>
          </Grid>
          <Typography
            sx={{
              mt: 3,
              mb: 4
            }}
            variant="body1"
            color="primary"
          >
            {t('routes.activate.main.subTitle')}
          </Typography>
          {error && (
            <Alert sx={{ my: 2 }} severity="error">
              {error}
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600 }} variant="body2">
              {t('routes.activate.main.subTitlePassword')}
            </Typography>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type={showPw ? 'text' : 'password'}
                  size="small"
                  fullWidth
                  disabled={isPending}
                  error={!!errors.password}
                  helperText={
                    errors.password?.message
                      ? t(errors.password.message as ActivateTranslationKey)
                      : undefined
                  }
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPw(s => !s)}
                            edge="end"
                            aria-label="toggle password visibility"
                          >
                            {showPw ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              )}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600 }} variant="body2">
              {t('routes.activate.main.subTitleConfirmPassword')}
            </Typography>
            <Controller
              name="confirm"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type={showConfirm ? 'text' : 'password'}
                  size="small"
                  fullWidth
                  disabled={isPending}
                  error={!!errors.confirm}
                  helperText={
                    errors.confirm?.message
                      ? t(errors.confirm.message as ActivateTranslationKey)
                      : undefined
                  }
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirm(s => !s)}
                            edge="end"
                            aria-label="toggle password visibility"
                          >
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              )}
            />
          </Box>

          <PasswordRequirementsList requirements={passwordRequirements} />

          <Button
            type="submit"
            variant="contained"
            sx={{ mt: 2, mb: 2, backgroundColor: '#0C2440' }}
            disabled={isPending || !isValid || !isPasswordValid}
          >
            {isPending
              ? t('routes.activate.main.buttonPendingText')
              : t('routes.activate.main.buttonText')}
          </Button>
        </Box>
      </UnauthenticatedPage>
    </AuthenticationPageContainer>
  )
}
